/**
 * @module
 *
 * @todo
 *
 *   - Implement Validation in sessionStore
 */

/**
 * @import {UserProfileData} from '../user/model/user-profile.js'
 * @import {ErrorResponse} from '../core/service/backend.js';
 * @import {UserProfile} from '../user/profile/profile.js';
 */
import {service} from 'nova';
import {guard} from '@acng/frontend-rubicon';
import {asWaitingImage, USER_PROFILE_DATA} from '../user/model/user-profile.js';
import {get, post, put} from '../core/service/backend.js';
import {sessionStore, USER_PROFILE} from './browser-storage.js';
import {undefined} from '@acng/frontend-bounty/std/value.js';
import {user} from 'acng/user/user.js';

class UserProfileApi {
  /**
   * @returns {Promise<UserProfileData>}
   */
  async get() {
    const data = await get('userProfile/view');
    ASSERT: guard(data, USER_PROFILE_DATA);

    return data;
  }

  /**
   * @param {Partial<UserProfileData>} partialData
   * @param {Element} [initiator]
   * @returns {Promise<UserProfileData>}
   */
  async put(partialData, initiator) {
    const data = await put('userProfile/update', partialData, initiator);
    ASSERT: guard(data, USER_PROFILE_DATA);

    return data;
  }
}

/**
 * Superseeded by {@link UserProfile}
 */
@service
class CachedUserProfileApi extends UserProfileApi {
  /**
   * @type {Promise<UserProfileData> | undefined}
   */
  #latestResult;

  /**
   * @type {UserProfileData | undefined}
   */
  #profile;

  /**
   * @returns {UserProfileData}
   *
   * @throws {CachedUserProfileApi} If the service has no profile data yet. Call
   *   {@link CachedUserProfileApi.get} first in that case.
   */
  get data() {
    const data = this.#profile;

    if (!data) {
      throw this;
    }

    return data;
  }

  /**
   * Flush all caches so that the next {@link CachedUserProfileApi.get} call will
   * perform an ACNG API request.
   */
  flush() {
    delete sessionStore[USER_PROFILE];
    this.#latestResult = undefined;
    this.#profile = undefined;
  }

  /**
   * 1. Use data from session store.
   * 2. If not available, use data from latest result.
   * 3. If not available, fetch data from ACNG API.
   * 4. Store data in session.
   *
   * The data is {@link guard | asserted} to be valid.
   *
   * @returns {Promise<UserProfileData>}
   *
   * @throws
   *
   *   - {@link ErrorResponse} When the user is not authenticated
   *
   * @override
   */
  async get() {
    let value = this.#profile;

    if (value) {
      return value;
    }

    value = sessionStore[USER_PROFILE];

    // TODO: Use watcher
    if (value && user.id != value.id) {
      this.flush();
      value = undefined;
    }

    if (value) {
      DEBUG: if ('DEBUG' in this && value)
        console.info('%cUse user profile from session store.', CONSOLE_STYLE, value);
      ASSERT: if (value) guard(value, USER_PROFILE_DATA);
      this.#profile = value;

      return value;
    }

    const fetching = this.#latestResult;

    if (!fetching) {
      const res = super.get();
      DEBUG: if ('DEBUG' in this) console.log('%cFetch user profile from ACNG API...', CONSOLE_STYLE, res);
      value = await this.#register(res);
    } else {
      const data = await fetching;
      value = data;
    }

    return value;
  }

  /**
   * @param {Promise<UserProfileData>} futureData
   * @returns {Promise<UserProfileData>}
   */
  async #register(futureData) {
    this.#latestResult = futureData;
    futureData.catch(() => this.#latestResult = undefined);
    const data = await futureData;
    DEBUG: if ('DEBUG' in this) console.info('%c... Got user profile from ACNG API.', CONSOLE_STYLE, data);
    this.#profile = data;
    sessionStore[USER_PROFILE] = data;

    return data;
  }

  /**
   * 1. Await previous fetch completion.
   * 2. Put data to ACNG API.
   * 3. Store updated profile in session.
   *
   * The data returned from the ACNG API is {@link guard | asserted} to be valid.
   *
   * @param {Partial<UserProfileData>} partialData
   * @param {Element} [initiator]
   * @returns {Promise<UserProfileData>}
   *
   * @override
   */
  async put(partialData, initiator) {
    await this.#latestResult;

    const res = super.put(partialData, initiator);
    DEBUG: if ('DEBUG' in this) console.log('%cPut user profile to ACNG API...', CONSOLE_STYLE, res);
    const data = await this.#register(res);

    return data;
  }
}

class UserProfileImageApi {
  async get() {
    const data = await get('userProfile/waiting-images');
    ASSERT: guard(data, asWaitingImage, '<waiting image>');

    return data;
  }

  /**
   * @param {FormData} formData
   * @param {Element} [initiator]
   *
   * @returns {Promise<void>}
   */
  async post(formData, initiator) {
    await post('userProfile/image', formData, initiator);
  }
}

const CONSOLE_STYLE = 'background: darkgreen; color: lightgreen; padding: 0 1pc; border-radius: 1pt';

export {CachedUserProfileApi as UserProfileApi, UserProfileImageApi};
